import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  Paper,
  TextField,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Collapse,
  Switch,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  CardActionArea,
  Avatar,
  Fade,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import FilterListIcon from '@mui/icons-material/FilterList';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { getCharacterImageUrl } from '../../../utils/mediaUrl';
import {
  basePostureOptions,
  bodyLanguagePresetOptions,
  getBodyLanguagePresetSettings,
  normalizeAnimationProfile,
} from '../../../utils/animationProfile';
import api from '../../../services/api';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  caseManagerPalette,
  caseManagerSelectSx,
  publicBodyFontFamily,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../caseManagerStyles';

const ageGroupOptions = ['18-30', '30-50', '50-75', '75+'];
const genderOptions = ['Male', 'Female'];
const ethnicAppearanceOptions = [
  'Caucasian',
  'South Asian',
  'East Asian',
  'Black - African',
  'Black - Caribbean',
  'Middle Eastern',
  'Mixed'
];
const bodyHabitusOptions = ['Underweight', 'Normal', 'Overweight', 'Obese'];

const sortOptions = [
  { value: 'recent', label: 'Most recently added' },
  { value: 'age-asc', label: 'Age (young → old)' },
  { value: 'age-desc', label: 'Age (old → young)' },
  { value: 'gender', label: 'Gender' },
  { value: 'ethnicity', label: 'Ethnic appearance' },
  { value: 'bodyHabitus', label: 'Body habitus' },
  { value: 'name', label: 'Name' }
];

const ageOrder = new Map([
  ['18-30', 0],
  ['30-50', 1],
  ['50-75', 2],
  ['75+', 3]
]);

const EMPTY_PREVIEW_TARGET = Object.freeze({ provider: '', voice: '' });
const CHARACTER_CARD_PREVIEW_MAX_WIDTH = 132;

const extractPreviewErrorMessage = async (error) => {
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      if (!text) {
        return 'Failed to load voice sample.';
      }
      try {
        const parsed = JSON.parse(text);
        if (parsed?.message) {
          return parsed.message;
        }
      } catch {
        return text;
      }
    } catch {
      return 'Failed to load voice sample.';
    }
  }

  return error?.response?.data?.message || error?.message || 'Failed to load voice sample.';
};

const CharacterSelectionStep = ({ data, presets, onUpdate, validationErrors = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filters, setFilters] = useState({
    ageGroup: [],
    genderPresentation: [],
    ethnicAppearance: [],
    bodyHabitus: []
  });
  const [previewError, setPreviewError] = useState('');
  const [previewLoadingTarget, setPreviewLoadingTarget] = useState(EMPTY_PREVIEW_TARGET);
  const [activePreviewTarget, setActivePreviewTarget] = useState(EMPTY_PREVIEW_TARGET);
  const [showAdvancedBodyLanguage, setShowAdvancedBodyLanguage] = useState(false);
  const audioRef = useRef(null);
  const previewUrlCacheRef = useRef(new Map());
  const animationProfile = useMemo(
    () => normalizeAnimationProfile(data.animationProfile),
    [data.animationProfile]
  );

  const hasFieldError = (fieldName) => {
    return validationErrors.some((error) =>
      error.toLowerCase().includes(fieldName.toLowerCase())
    );
  };

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const active = prev[key] || [];
      const exists = active.includes(value);
      const nextValues = exists ? active.filter((item) => item !== value) : [...active, value];
      return { ...prev, [key]: nextValues };
    });
  };

  const clearFilters = () => {
    setFilters({
      ageGroup: [],
      genderPresentation: [],
      ethnicAppearance: [],
      bodyHabitus: []
    });
    setSearchTerm('');
    setSortBy('recent');
  };

  const selectedCharacter = useMemo(() => {
    if (!Array.isArray(presets.characters)) return null;
    return presets.characters.find((char) => char._id === data.characterId) || null;
  }, [presets.characters, data.characterId]);

  const filteredCharacters = useMemo(() => {
    const base = Array.isArray(presets.characters) ? presets.characters : [];
    const search = searchTerm.trim().toLowerCase();

    const matchesFilters = (character) => {
      const ageOk =
        filters.ageGroup.length === 0 ||
        (character.ageGroup && filters.ageGroup.includes(character.ageGroup));
      const genderOk =
        filters.genderPresentation.length === 0 ||
        (character.genderPresentation && filters.genderPresentation.includes(character.genderPresentation));
      const ethnicOk =
        filters.ethnicAppearance.length === 0 ||
        (character.ethnicAppearance && filters.ethnicAppearance.includes(character.ethnicAppearance));
      const habitusOk =
        filters.bodyHabitus.length === 0 ||
        (character.bodyHabitus && filters.bodyHabitus.includes(character.bodyHabitus));
      return ageOk && genderOk && ethnicOk && habitusOk;
    };

    const applySearch = (character) => {
      if (!search) return true;
      const haystack = [
        character.name,
        character.description,
        character.ageGroup,
        character.genderPresentation,
        character.ethnicAppearance,
        character.bodyHabitus
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    };

    const getAgeIndex = (value) => {
      if (!value) return ageOrder.size;
      return ageOrder.get(value) ?? ageOrder.size;
    };

    const sorters = {
      'age-asc': (a, b) => getAgeIndex(a.ageGroup) - getAgeIndex(b.ageGroup),
      'age-desc': (a, b) => getAgeIndex(b.ageGroup) - getAgeIndex(a.ageGroup),
      gender: (a, b) => (a.genderPresentation || '').localeCompare(b.genderPresentation || ''),
      ethnicity: (a, b) => (a.ethnicAppearance || '').localeCompare(b.ethnicAppearance || ''),
      bodyHabitus: (a, b) => (a.bodyHabitus || '').localeCompare(b.bodyHabitus || ''),
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      recent: (a, b) => {
        const aDate = new Date(a.createdAt || 0).getTime();
        const bDate = new Date(b.createdAt || 0).getTime();
        return bDate - aDate;
      }
    };

    const sorter = sorters[sortBy] || sorters.recent;

    return base
      .filter(matchesFilters)
      .filter(applySearch)
      .slice()
      .sort(sorter);
  }, [presets.characters, filters, searchTerm, sortBy]);

  const handleCharacterSelect = (characterId) => {
    onUpdate({ characterId });
  };

  const handleOpenAIVoiceModelChange = (openAIVoiceModel) => {
    onUpdate({ openAIVoiceModel });
  };

  const handleGeminiVoiceModelChange = (geminiVoiceModel) => {
    onUpdate({ geminiVoiceModel });
  };

  const handleAnimationProfileChange = (updates) => {
    onUpdate({
      animationProfile: normalizeAnimationProfile({
        ...animationProfile,
        ...updates,
      }),
    });
  };

  const handleBodyLanguageStyleChange = (bodyLanguagePreset) => {
    handleAnimationProfileChange({
      bodyLanguagePreset,
      ...getBodyLanguagePresetSettings(bodyLanguagePreset),
    });
  };

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    previewUrlCacheRef.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    previewUrlCacheRef.current.clear();
  }, []);

  const stopVoicePreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    setActivePreviewTarget(EMPTY_PREVIEW_TARGET);
  };

  const playVoicePreview = async ({ provider, voice }) => {
    if (!provider || !voice) {
      return;
    }

    setPreviewError('');
    setPreviewLoadingTarget({ provider, voice });

    try {
      const cacheKey = `${provider}:${voice}`;
      let sampleUrl = previewUrlCacheRef.current.get(cacheKey) || '';
      if (!sampleUrl) {
        const response = await api.get('/platform/voices/preview', {
          params: { provider, voice },
          responseType: 'blob',
        });
        sampleUrl = URL.createObjectURL(response.data);
        previewUrlCacheRef.current.set(cacheKey, sampleUrl);
      }

      if (!audioRef.current) {
        const audio = new Audio();
        audio.onended = () => {
          setActivePreviewTarget(EMPTY_PREVIEW_TARGET);
        };
        audioRef.current = audio;
      }

      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = sampleUrl;
      await audioRef.current.play();
      previewUrlCacheRef.current.set(cacheKey, sampleUrl);
      setActivePreviewTarget({ provider, voice });
    } catch (error) {
      stopVoicePreview();
      const message = error?.message || await extractPreviewErrorMessage(error);
      setPreviewError(message);
    } finally {
      setPreviewLoadingTarget(EMPTY_PREVIEW_TARGET);
    }
  };

  const toggleVoicePreview = async ({ provider, voice }) => {
    if (!provider || !voice) {
      return;
    }

    const isActivePreview =
      activePreviewTarget.provider === provider
      && activePreviewTarget.voice === voice;

    if (isActivePreview) {
      stopVoicePreview();
      return;
    }

    await playVoicePreview({ provider, voice });
  };

  const isGeminiPreviewLoading =
    previewLoadingTarget.provider === 'gemini'
    && previewLoadingTarget.voice === (data.geminiVoiceModel || '');
  const isOpenAiPreviewLoading =
    previewLoadingTarget.provider === 'openai'
    && previewLoadingTarget.voice === (data.openAIVoiceModel || '');
  const isGeminiPreviewActive =
    activePreviewTarget.provider === 'gemini'
    && activePreviewTarget.voice === (data.geminiVoiceModel || '');
  const isOpenAiPreviewActive =
    activePreviewTarget.provider === 'openai'
    && activePreviewTarget.voice === (data.openAIVoiceModel || '');

  const renderFilterGroup = (label, key, options) => (
    <Box sx={{ mb: { xs: 1, lg: 0 }, minWidth: { xs: '100%', md: 0 } }}>
      <Typography variant="subtitle2" sx={{ mb: 0.65, fontWeight: 700, color: caseManagerPalette.ink, fontFamily: publicBodyFontFamily }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
        {options.map((option) => {
          const active = filters[key].includes(option);
          return (
            <Chip
              key={option}
              label={option}
              clickable
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              size="small"
              onClick={() => toggleFilter(key, option)}
              sx={{ 
                borderRadius: '999px',
                borderColor: active ? 'transparent' : alpha(caseManagerPalette.ink, 0.10),
                backgroundColor: active ? caseManagerPalette.heroBlueDeep : 'transparent',
                color: active ? caseManagerPalette.white : caseManagerPalette.inkSoft,
                fontFamily: publicBodyFontFamily,
                fontWeight: 700,
                '&:hover': {
                    backgroundColor: active ? caseManagerPalette.heroBlueDeeper : alpha(caseManagerPalette.heroBlueMid, 0.06)
                }
              }}
            />
          );
        })}
      </Box>
    </Box>
  );

  const anyFilters =
    searchTerm.trim().length > 0 ||
    filters.ageGroup.length > 0 ||
    filters.genderPresentation.length > 0 ||
    filters.ethnicAppearance.length > 0 ||
    filters.bodyHabitus.length > 0;

  const selectedBodyLanguageStyle = bodyLanguagePresetOptions.find(
    (option) => option.value === animationProfile.bodyLanguagePreset
  ) || bodyLanguagePresetOptions[0];

  return (
    <CaseEditorStepFrame>
      <Grid container spacing={1.8}>
        {/* Filters */}
        <Grid item xs={12}>
          <Paper 
            elevation={0} 
            sx={{ 
              ...caseManagerInsetSurfaceSx,
              p: { xs: 1.4, md: 1.6 },
              borderRadius: '18px',
            }}
          >
            <Stack spacing={{ xs: 1.4, md: 1.2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.2, sm: 1.6 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { sm: 136 } }}>
                  <FilterListIcon sx={{ mr: 1, color: caseManagerPalette.heroBlueMid }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: caseManagerPalette.ink, fontFamily: publicBodyFontFamily }}>
                    Filters
                  </Typography>
                </Box>

                <TextField
                  placeholder="Search by name, tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{
                    ...publicTextFieldSx,
                    flex: 1,
                    maxWidth: { sm: 420 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  disabled={!anyFilters}
                  sx={{
                    ...caseManagerSelectSx,
                    borderRadius: '999px',
                    minWidth: { sm: 150 },
                    ml: { sm: 'auto !important' },
                  }}
                >
                  Clear filters
                </Button>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: { xs: 1, md: 1.2 },
                  minWidth: 0,
                }}
              >
                {renderFilterGroup('Age Group', 'ageGroup', ageGroupOptions)}
                {renderFilterGroup('Gender', 'genderPresentation', genderOptions)}
                {renderFilterGroup('Ethnicity', 'ethnicAppearance', ethnicAppearanceOptions)}
                {renderFilterGroup('Body Habitus', 'bodyHabitus', bodyHabitusOptions)}
              </Box>
            </Stack>

            {hasFieldError('character') && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center', fontWeight: 'bold', fontFamily: publicBodyFontFamily }}>
                * Character selection is required
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Middle - Character Grid */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.2,
              mb: 1.5,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: caseManagerPalette.ink, fontFamily: publicBodyFontFamily }}>
              Available Characters 
              <Chip label={filteredCharacters.length} size="small" sx={{ ml: 1.5, bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.10), color: caseManagerPalette.heroBlueDeep, fontWeight: 700, fontFamily: publicBodyFontFamily }} />
            </Typography>
            
            <FormControl size="small" sx={{ ...caseManagerSelectSx, minWidth: 180 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                label="Sort by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              maxHeight: { md: 'min(64vh, 680px)' },
              overflowY: { md: 'auto' },
              pr: { md: 0.5 },
              pb: 0.5,
            }}
          >
            <Grid container spacing={1.4}>
              {filteredCharacters.map((character) => {
                const isSelected = data.characterId === character._id;
                return (
                  <Grid item xs={6} sm={4} lg={3} key={character._id}>
                    <Card
                      elevation={isSelected ? 4 : 1}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        border: isSelected ? `2px solid ${caseManagerPalette.heroBlueMid}` : `1px solid ${alpha(caseManagerPalette.ink, 0.08)}`,
                        backgroundColor: alpha('#ffffff', 0.9),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                          borderColor: isSelected ? caseManagerPalette.heroBlueMid : alpha(caseManagerPalette.heroBlueMid, 0.36)
                        }
                      }}
                    >
                      <CardActionArea
                          onClick={() => handleCharacterSelect(character._id)}
                          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
                      >
                          {character.profilePicture ? (
                          <CardMedia
                              component="img"
                              image={getCharacterImageUrl(character.profilePicture)}
                              alt={character.name}
                              sx={{
                                width: '100%',
                                maxWidth: CHARACTER_CARD_PREVIEW_MAX_WIDTH,
                                mx: 'auto',
                                aspectRatio: '1 / 1',
                                objectFit: 'cover',
                              }}
                          />
                          ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  maxWidth: CHARACTER_CARD_PREVIEW_MAX_WIDTH,
                                  mx: 'auto',
                                  aspectRatio: '1 / 1',
                                  bgcolor: 'grey.200',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                  <PersonIcon sx={{ fontSize: 44, color: 'grey.400' }} />
                              </Box>
                          )}
                          <CardContent sx={{ flexGrow: 1, p: 1.1 }}>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.92rem', lineHeight: 1.25, fontWeight: 800, mb: 0.4, fontFamily: publicBodyFontFamily, color: caseManagerPalette.ink }}>
                              {character.name}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.45, mt: 1 }}>
                              {character.ageGroup && (
                              <Chip label={character.ageGroup} size="small" sx={{ height: 19, fontSize: '0.68rem', fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                              )}
                              {character.genderPresentation && (
                              <Chip label={character.genderPresentation} size="small" sx={{ height: 19, fontSize: '0.68rem', fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                              )}
                          </Box>
                          </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {filteredCharacters.length === 0 && (
            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 2.4, textAlign: 'center', mt: 1.5, borderStyle: 'dashed', borderRadius: '16px' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No characters found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try adjusting your filters or search terms.
              </Typography>
              <Button sx={publicSecondaryButtonSx} variant="outlined" onClick={clearFilters}>
                Reset All Filters
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Right Sidebar - Selection Details */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: 18 }}>
            <Paper 
                elevation={3}
                sx={{ 
                    ...caseManagerInsetSurfaceSx,
                    p: 0, 
                    overflow: 'hidden',
                    borderRadius: '16px',
                }}
            >
                <Box sx={{ p: 1.35, bgcolor: caseManagerPalette.heroBlueDeep, color: 'white' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', fontFamily: publicBodyFontFamily }}>
                        <PersonIcon sx={{ mr: 1 }} /> Selected Profile
                    </Typography>
                </Box>
                
                <Box sx={{ p: 1.6 }}>
                    {selectedCharacter ? (
                        <Fade in={true}>
                            <Stack spacing={1.4}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                                    <Avatar 
                                        src={getCharacterImageUrl(selectedCharacter.profilePicture)} 
                                        sx={{ width: 78, height: 78, mb: 1.2, boxShadow: 2 }}
                                    />
                                    <Typography variant="subtitle1" align="center" sx={{ fontWeight: 800, fontFamily: publicBodyFontFamily, color: caseManagerPalette.ink }}>
                                        {selectedCharacter.name}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                        Details
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                        {selectedCharacter.ageGroup && <Chip label={selectedCharacter.ageGroup} size="small" variant="outlined" sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />}
                                        {selectedCharacter.genderPresentation && (
                                        <Chip label={selectedCharacter.genderPresentation} size="small" variant="outlined" sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                                        )}
                                        {selectedCharacter.ethnicAppearance && (
                                        <Chip label={selectedCharacter.ethnicAppearance} size="small" variant="outlined" sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                                        )}
                                        {selectedCharacter.bodyHabitus && (
                                        <Chip label={selectedCharacter.bodyHabitus} size="small" variant="outlined" sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                                        )}
                                    </Box>
                                </Box>

                                {selectedCharacter.description && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                            Description
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.45 }}>
                                            {selectedCharacter.description}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Fade>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4, opacity: 0.6 }}>
                            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                No character selected.
                                <br />
                                Click a card to select.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Paper
                elevation={3}
                sx={{
                    ...caseManagerInsetSurfaceSx,
                    mt: 1.4,
                    p: 0,
                    overflow: 'hidden',
                    borderRadius: '16px',
                }}
            >
                <Box sx={{ p: 1.35, bgcolor: caseManagerPalette.heroBlueMid, color: 'white' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', fontFamily: publicBodyFontFamily }}>
                        <PersonIcon sx={{ mr: 1 }} /> Body Language
                    </Typography>
                </Box>
                <Box sx={{ p: 1.6 }}>
                    <Stack spacing={1.35}>
                      <FormControl fullWidth size="small" variant="outlined" sx={caseManagerSelectSx}>
                        <InputLabel>Body-language style</InputLabel>
                        <Select
                          label="Body-language style"
                          value={animationProfile.bodyLanguagePreset}
                          onChange={(e) => handleBodyLanguageStyleChange(e.target.value)}
                        >
                          {bodyLanguagePresetOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Typography
                        variant="body2"
                        sx={{ color: caseManagerPalette.inkSoft, lineHeight: 1.5, fontFamily: publicBodyFontFamily }}
                      >
                        {selectedBodyLanguageStyle.description} Speaking, listening, and idle movement are adjusted automatically.
                      </Typography>

                      <Button
                        variant="text"
                        size="small"
                        aria-expanded={showAdvancedBodyLanguage}
                        onClick={() => setShowAdvancedBodyLanguage((current) => !current)}
                        sx={{ alignSelf: 'flex-start', px: 0.4, fontWeight: 700, textTransform: 'none' }}
                      >
                        {showAdvancedBodyLanguage ? 'Hide advanced options' : 'Advanced options'}
                      </Button>

                      <Collapse in={showAdvancedBodyLanguage} unmountOnExit>
                        <Stack spacing={1.25} sx={{ pt: 0.25 }}>
                          <Divider />
                          <FormControl fullWidth size="small" variant="outlined" sx={caseManagerSelectSx}>
                            <InputLabel>Posture override</InputLabel>
                            <Select
                              label="Posture override"
                              value={animationProfile.basePosture}
                              onChange={(e) => handleAnimationProfileChange({ basePosture: e.target.value })}
                            >
                              {basePostureOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography
                            variant="caption"
                            sx={{ color: caseManagerPalette.inkSoft, lineHeight: 1.45, fontFamily: publicBodyFontFamily }}
                          >
                            Auto lets the selected style choose an appropriate posture for each session.
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={animationProfile.semanticReactionsEnabled}
                                onChange={(e) => handleAnimationProfileChange({ semanticReactionsEnabled: e.target.checked })}
                                color="primary"
                              />
                            }
                            label="Use natural nods and head shakes for clear yes/no answers"
                            sx={{
                              m: 0,
                              color: caseManagerPalette.ink,
                              alignItems: 'flex-start',
                              '& .MuiSwitch-root': { mt: -0.45 },
                              '& .MuiFormControlLabel-label': {
                                fontFamily: publicBodyFontFamily,
                                fontWeight: 700,
                                lineHeight: 1.35,
                              },
                            }}
                          />
                        </Stack>
                      </Collapse>
                    </Stack>
                </Box>
            </Paper>

            <Paper 
                elevation={3}
                sx={{ 
                    ...caseManagerInsetSurfaceSx,
                    mt: 1.4,
                    p: 0, 
                    overflow: 'hidden',
                    borderRadius: '16px',
                }}
            >
                <Box sx={{ p: 1.35, bgcolor: caseManagerPalette.ink, color: 'white' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', fontFamily: publicBodyFontFamily }}>
                        <RecordVoiceOverIcon sx={{ mr: 1 }} /> Voice Configuration
                    </Typography>
                </Box>
                <Box sx={{ p: 1.6 }}>
                    <FormControl fullWidth size="small" error={hasFieldError('gemini voice')} variant="outlined" sx={{ ...caseManagerSelectSx, mb: 1.4 }}>
                      <InputLabel>Gemini Voice</InputLabel>
                      <Select
                        label="Gemini Voice"
                        value={data.geminiVoiceModel || ''}
                        onChange={(e) => handleGeminiVoiceModelChange(e.target.value)}
                      >
                        {(Array.isArray(presets.geminiVoiceModels) ? presets.geminiVoiceModels : []).map((voice) => (
                          <MenuItem key={voice.id} value={voice.id}>
                            {voice.name || voice.id}
                          </MenuItem>
                        ))}
                      </Select>
                      {hasFieldError('gemini voice') && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                          * Gemini voice model is required
                        </Typography>
                      )}
                    </FormControl>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.4 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleVoicePreview({ provider: 'gemini', voice: data.geminiVoiceModel || '' })}
                        disabled={!data.geminiVoiceModel || Boolean(previewLoadingTarget.provider)}
                        startIcon={
                          isGeminiPreviewLoading ? (
                            <CircularProgress color="inherit" size={14} />
                          ) : isGeminiPreviewActive ? (
                            <StopIcon fontSize="small" />
                          ) : (
                            <PlayArrowIcon fontSize="small" />
                          )
                        }
                      >
                        {isGeminiPreviewActive ? 'Stop sample' : 'Play sample'}
                      </Button>
                    </Box>

                    <FormControl fullWidth size="small" error={hasFieldError('openai voice')} variant="outlined" sx={{ ...caseManagerSelectSx, mb: 1.4 }}>
                      <InputLabel>OpenAI Voice</InputLabel>
                      <Select
                        label="OpenAI Voice"
                        value={data.openAIVoiceModel || ''}
                        onChange={(e) => handleOpenAIVoiceModelChange(e.target.value)}
                      >
                        {(Array.isArray(presets.voiceModels) ? presets.voiceModels : []).map((voice) => (
                          <MenuItem key={voice.id} value={voice.id}>
                            {voice.name || voice.id}
                          </MenuItem>
                        ))}
                      </Select>
                      {hasFieldError('openai voice') && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                          * OpenAI voice model is required
                        </Typography>
                      )}
                    </FormControl>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleVoicePreview({ provider: 'openai', voice: data.openAIVoiceModel || '' })}
                        disabled={!data.openAIVoiceModel || Boolean(previewLoadingTarget.provider)}
                        startIcon={
                          isOpenAiPreviewLoading ? (
                            <CircularProgress color="inherit" size={14} />
                          ) : isOpenAiPreviewActive ? (
                            <StopIcon fontSize="small" />
                          ) : (
                            <PlayArrowIcon fontSize="small" />
                          )
                        }
                      >
                        {isOpenAiPreviewActive ? 'Stop sample' : 'Play sample'}
                      </Button>
                    </Box>

                    {previewError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                        {previewError}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      OpenAI sessions use the OpenAI voice and Gemini sessions use the Gemini voice.
                    </Typography>
                </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </CaseEditorStepFrame>
  );

};

export default CharacterSelectionStep;
